import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import type { ReactNode } from 'react';

export interface ModalShellProps {
  title: string;
  /** The sub-line under the title — "الحقول المعلّمة بنجمة مطلوبة". */
  description: string;
  closeLabel: string;
  children: ReactNode;
  /** Action buttons. The caller supplies them so each mode can differ. */
  footer?: ReactNode;
  /** Uncontrolled use: the element that opens the modal. */
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

/**
 * The one modal the admin console has, in the prototype's shape: header with a title and
 * a hint, a scrolling body, and a footer of actions on a sideline background.
 *
 * The prototype ran every mode — view, create, edit, reject, delete — through a single
 * `modal` object with `x-if` branches inside it. That is the right *visual* decision and
 * the wrong code decision: the branches share nothing but a border. Here the shell is
 * the shared part and each screen composes its own body and footer into it, so the
 * reject form and the delete confirmation stop being the same component.
 */
export function ModalShell({
  title,
  description,
  closeLabel,
  children,
  footer,
  trigger,
  open,
  onOpenChange,
  className,
}: ModalShellProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

      <DialogContent closeLabel={closeLabel} className={className}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <DialogBody>{children}</DialogBody>

        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
